import type { VariantProps } from "class-variance-authority";
import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import { useInterval } from "@vueuse/core";
import { cva } from "class-variance-authority";
import { ToastClose, ToastProvider, ToastRoot, ToastTitle, ToastViewport } from "reka-ui";
import { computed, defineComponent, nextTick, watch } from "vue";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";

const Provider = ToastProvider;

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-(--reka-toast-swipe-end-x) data-[swipe=move]:translate-x-(--reka-toast-swipe-move-x) data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: "border bg-popover text-foreground",
        destructive:
          "destructive group border-destructive bg-destructive text-destructive-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

type ToastVariant = VariantProps<typeof toastVariants>;

const toastProps = {
  type: p.string<"background" | "foreground">(),
  defaultOpen: p.boolean(),
  duration: p.number(),
  forceMount: p.boolean(),
  open: p.boolean(),
  variant: p.string<ToastVariant["variant"]>("default"),
  durationIndicatior: p.boolean(),
} satisfies ComponentPropsOptions;

export type ToastProps = ExtractPublicPropTypes<typeof toastProps>;

const Root = defineComponent({
  name: "ToastRoot",
  emits: {
    close: () => true,
    escapeKeyDown: (_e: Event) => true,
    pause: () => true,
    resume: () => true,
    swipeCancel: (_e: CustomEvent) => true,
    swipeEnd: (_e: CustomEvent) => true,
    swipeMove: (_e: CustomEvent) => true,
    swipeStart: (_e: CustomEvent) => true,
    "update:open": (_e: boolean) => true,
  },
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
    ...toastProps,
  },
  setup(props, { slots, emit, attrs }) {
    const duration = computed(() => {
      return props.duration ?? 5000;
    });

    const { counter, pause, resume } = useInterval(10, {
      controls: true,
      immediate: props.durationIndicatior,
    });

    if (props.durationIndicatior) {
      watch(counter, (val) => {
        if (val === duration.value / 10) {
          pause();
        }
      });
    }

    const progress = computed(() => {
      const percentage = (counter.value * 10) / duration.value;
      return percentage * 100;
    });

    return () => (
      <ToastRoot
        {...props}
        onUpdate:open={(e: boolean) => {
          emit("update:open", e);
        }}
        forceMount={props.forceMount ? true : undefined}
        onEscapeKeyDown={(e: Event) => emit("escapeKeyDown", e)}
        onSwipeCancel={(e: CustomEvent) => emit("swipeCancel", e)}
        onPause={() => {
          pause();
          emit("pause");
        }}
        onResume={() => {
          emit("resume");
          counter.value -= 7;
          nextTick(resume);
        }}
        onSwipeEnd={(e: CustomEvent) => emit("swipeEnd", e)}
        onSwipeMove={(e: CustomEvent) => emit("swipeMove", e)}
        onSwipeStart={(e: CustomEvent) => emit("swipeStart", e)}
        class={cn(toastVariants(props), "", attrs.class ?? "")}
      >
        <div class="flex w-full justify-between">{slots.default?.()}</div>
        {props.durationIndicatior && (
          <div
            class="absolute -left-2 bottom-0 h-0.5 bg-brand-9"
            style={{ width: progress.value + "%" }}
          ></div>
        )}
      </ToastRoot>
    );
  },
});

const Viewport = defineComponent({
  name: "ToastViewport",
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),

    hotkey: p.array<string[]>([]),
    label: p.string(),
  },
  setup(props, { attrs }) {
    return () => (
      <ToastViewport
        hotkey={props.hotkey}
        as={props.as}
        asChild={props.asChild}
        label={props.label}
        class={cn(
          "fixed bottom-0 z-1000 flex w-full flex-col space-y-2 p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
          attrs.class ?? ""
        )}
      ></ToastViewport>
    );
  },
});

const Action = defineComponent({
  name: "ToastAction",
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
    class: p.string(),
    altText: p.$string(),
  },
  setup(props, { slots }) {
    return () => (
      <div {...props} class={cn(props.class)}>
        {slots.default?.()}
      </div>
    );
  },
});

const Close = defineComponent({
  name: "ToastClose",
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
    class: p.string(),
  },
  setup(props, { slots }) {
    return () => (
      <ToastClose
        {...props}
        class={cn(
          "group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600 absolute right-1 top-1 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-hidden focus:ring-1 group-hover:opacity-100",
          props.class
        )}
      >
        {slots.default?.()}
      </ToastClose>
    );
  },
});

const Title = defineComponent({
  name: "ToastTitle",
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
    class: p.string(),
  },
  setup(props, { slots }) {
    return () => (
      <ToastTitle {...props} class={cn("text-sm font-semibold [&+div]:text-xs", props.class)}>
        {slots.default?.()}
      </ToastTitle>
    );
  },
});

const Description = defineComponent({
  name: "ToastDescription",
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(),
    class: p.string(),
  },
  setup(props, { slots }) {
    return () => (
      <ToastTitle {...props} class={cn("text-sm opacity-90", props.class)}>
        {slots.default?.()}
      </ToastTitle>
    );
  },
});

export default {
  Provider,
  Root,
  Viewport,
  Action,
  Close,
  Title,
  Description,
};
